import * as React from 'react'

import { ChevronDown } from 'lucide-react'
import { classList } from 'react-classlist-helper'

// localization: no text to translate

const DEFAULT_MAX_LINES = 2
const FALLBACK_LINE_HEIGHT = 20
const ELLIPSIS = '…'

interface IProps {
  text: string

  className?: string
  maxLines?: number
  /** Native tooltip while the text is clamped. Omit to show none. */
  title?: string
}

interface IState {
  isExpanded: boolean
  isOverflowing: boolean
  clampedText: string
}

// Lives inside the span so it flows right after the (truncated) text and
// inherits its color.
const Chevron = ({ isExpanded }: { isExpanded: boolean }): JSX.Element => (
  <ChevronDown
    aria-hidden="true"
    className={classList({
      'tw:inline-block tw:size-4 tw:align-middle tw:ml-0.5 tw:transition-transform': true,
      'tw:rotate-180': isExpanded,
    })}
  />
)

/**
 * Text clamped to `maxLines` with an inline chevron that reveals the rest.
 * Used for field labels/captions long enough to push their content out of view.
 */
export default class ExpandableText extends React.PureComponent<
  IProps,
  IState
> {
  public state: IState = {
    isExpanded: false,
    isOverflowing: false,
    clampedText: '',
  }

  private readonly textRef = React.createRef<HTMLSpanElement>()
  private resizeObserver: ResizeObserver = null

  public componentDidMount() {
    const el = this.textRef.current
    if (!el) return

    this.clampToMaxLines()
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.clampToMaxLines())
      this.resizeObserver.observe(el)
    }
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (prevProps.text !== this.props.text) {
      this.setState(
        { isExpanded: false, isOverflowing: false, clampedText: '' },
        this.clampToMaxLines,
      )
      return
    }
    if (
      prevState.isOverflowing !== this.state.isOverflowing ||
      prevState.isExpanded !== this.state.isExpanded
    ) {
      this.clampToMaxLines()
    }
  }

  public componentWillUnmount() {
    this.resizeObserver?.disconnect()
  }

  public render() {
    const { text, className, title } = this.props
    const { isExpanded, isOverflowing, clampedText } = this.state
    const displayText =
      isExpanded || !isOverflowing || !clampedText ? text : clampedText

    return (
      <span
        ref={this.textRef}
        className={classList({
          [className]: !!className,
          // Read-only/disabled inputs get `pointer-events: none` on an ancestor,
          // which would leave the chevron visible but unclickable.
          'tw:cursor-pointer tw:pointer-events-auto': isOverflowing,
        })}
        title={isOverflowing && !isExpanded ? title : undefined}
        role={isOverflowing ? 'button' : undefined}
        tabIndex={isOverflowing ? 0 : undefined}
        aria-expanded={isOverflowing ? isExpanded : undefined}
        onClick={isOverflowing ? this.toggleExpanded : undefined}
        onKeyDown={isOverflowing ? this.onKeyDown : undefined}
      >
        {displayText}
        {isOverflowing && <Chevron isExpanded={isExpanded} />}
      </span>
    )
  }

  // Trims the text to the longest prefix that (with the inline chevron) still
  // fits maxLines, so the chevron sits right after the ellipsis instead of far
  // to the right. Measures on the real element via its text node, leaving the
  // chevron in place, so the fit check accounts for it.
  private clampToMaxLines = () => {
    const el = this.textRef.current
    if (!el || this.state.isExpanded) return

    const textNode = el.firstChild
    if (!textNode) return

    const lineHeight =
      parseFloat(getComputedStyle(el).lineHeight) || FALLBACK_LINE_HEIGHT
    const maxLines = this.props.maxLines ?? DEFAULT_MAX_LINES
    const maxHeight = lineHeight * maxLines + 1
    const { text } = this.props

    textNode.nodeValue = text
    if (el.scrollHeight <= maxHeight) {
      if (this.state.isOverflowing) {
        this.setState({ isOverflowing: false, clampedText: '' })
      }
      return
    }

    // Ensure the chevron is rendered before measuring prefixes against it.
    if (!this.state.isOverflowing) {
      this.setState({ isOverflowing: true })
      return
    }

    let lo = 0
    let hi = text.length
    let best = 0
    while (lo <= hi) {
      const mid = (lo + hi) >> 1
      textNode.nodeValue = text.slice(0, mid).trimEnd() + ELLIPSIS
      if (el.scrollHeight <= maxHeight) {
        best = mid
        lo = mid + 1
      } else {
        hi = mid - 1
      }
    }

    const clampedText = text.slice(0, best).trimEnd() + ELLIPSIS
    if (clampedText !== this.state.clampedText) {
      this.setState({ clampedText })
    } else {
      textNode.nodeValue = clampedText
    }
  }

  // Toggling must not reach an interactive ancestor: some fields wrap the whole
  // labelled input in their own trigger (e.g. DynamicForm's DateTimeField opens a
  // date picker), which would otherwise fire alongside the expand.
  private toggleExpanded = (e?: React.SyntheticEvent) => {
    e?.stopPropagation()
    this.setState(prev => ({ isExpanded: !prev.isExpanded }))
  }

  private onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.toggleExpanded(e)
    }
  }
}
